import * as React from 'react'

import {
  CheckIcon,
  ChevronDownIcon,
  CircleUserRoundIcon,
  EllipsisIcon,
  LogOutIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { IPermitType } from '~/client/graph/types.generated'
import * as Icons from '~/client/src/shared/components/Icons'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import FormTypeSelectionForm from '~/client/src/shared/components/SitePermitCreationForm/FormTypeSelectionForm'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { GenericWorkflowIcon } from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowIconGroups'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IProcoreProject } from '~/client/src/shared/services/ProcoreService'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProcoreIntegrationStore from '~/client/src/shared/stores/domain/ProcoreStores/ProcoreIntegration.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import InitialState from '~/client/src/shared/stores/InitialState'

import FieldMappingTable from './FieldMappingTable'
import MultiSelectDropdown from './MultiSelectDropdown'
import ProcoreIntegrationSectionStore, {
  IWorkflowField,
} from './ProcoreIntegrationSection.store'
import StruxHubFieldSelectorModal from './StruxHubFieldSelectorModal'

import './ProcoreIntegrationSection.scss'

interface IProps {
  procoreIntegrationStore?: ProcoreIntegrationStore
  locationAttributesStore?: LocationAttributesStore
  workflowTypesStore?: WorkflowTypesStore
  state?: InitialState
}

@inject(
  'procoreIntegrationStore',
  'locationAttributesStore',
  'workflowTypesStore',
  'state',
)
@observer
export default class ProcoreIntegrationSection extends React.Component<IProps> {
  private store: ProcoreIntegrationSectionStore

  public constructor(props: IProps) {
    super(props)
    this.store = new ProcoreIntegrationSectionStore(
      props.procoreIntegrationStore,
      props.workflowTypesStore,
    )
  }

  public componentDidMount(): void {
    this.props.procoreIntegrationStore.reloadProcoreData()
  }

  public componentWillUnmount(): void {
    this.store.dispose()
  }

  private get procoreStore(): ProcoreIntegrationStore {
    return this.props.procoreIntegrationStore
  }

  public render(): JSX.Element {
    return (
      <div className="row full-width align-start bg-white mb20 procore-integration-section">
        <div className="ba-palette-brand-lighter pa15 mw590 relative">
          {this.renderHeader()}
          {this.renderLoginSection()}
          {this.procoreStore.isUserLoggedIn && (
            <>
              {this.renderCompanySelector()}
              {this.renderProjectSelector()}
              {this.renderWorkflowsSyncSection()}
            </>
          )}
        </div>
      </div>
    )
  }

  private renderHeader = (): JSX.Element => {
    const { isUserLoggedIn, isIntegrationActive } = this.procoreStore
    const { toggleTitle } = this.store

    return (
      <div className="row x-between pb15 bb-neutral-96">
        <div className="row gap-10 h40">
          <Icons.ProcoreLogo className="col icon-w40 no-grow" />
          <div className="col y-between">
            <p className="text large bold brand-dark">Procore</p>
            <div className="text small medium-bold grey">
              {Localization.translator.procoreDescription}
            </div>
          </div>
        </div>
        <div
          className="tw:flex tw:items-center tw:justify-end tw:gap-2.5"
          title={toggleTitle}
        >
          <div
            className={classList({
              'text small medium-bold grey no-white-space-wrap no-grow': true,
              'brand-dark': isUserLoggedIn,
            })}
          >
            {isIntegrationActive
              ? Localization.translator.integrationEnabled
              : Localization.translator.integrationDisabled}
          </div>
          <Switch
            checked={isIntegrationActive}
            onCheckedChange={this.store.handleToggleIntegration}
            disabled={!isUserLoggedIn}
          />
        </div>
      </div>
    )
  }

  private renderLoginSection = (): JSX.Element => {
    const { isUserLoggedIn, procoreUser, isInitializing } = this.procoreStore
    const { loginBtnTitle } = this.store
    return (
      <div
        className={classList({
          'row x-between pt15': true,
          'bb-neutral-96 pb15': isUserLoggedIn,
        })}
      >
        <div className="row gap-10 h40">
          <div className="row x-center brada4 mw40 h40 bg-palette-brand-lightest">
            {isUserLoggedIn ? (
              <div className="text center large bold brand-dark">
                {this.store.getUserInitials(procoreUser.name)}
              </div>
            ) : (
              <CircleUserRoundIcon
                size={20}
                className="tw:text-muted-foreground"
              />
            )}
          </div>
          <div className="col y-between">
            <p className="text large bold brand-dark">
              {procoreUser?.name || Localization.translator.loginToProcore}
            </p>
            <div className="text small medium-bold grey">
              {procoreUser?.login ||
                Localization.translator.procoreLoginDescription}
            </div>
          </div>
        </div>
        <div className="tw:flex tw:items-center tw:justify-end no-grow">
          <Button
            variant="outline"
            className={classList({
              'login-button': true,
              logout: isUserLoggedIn,
            })}
            onClick={this.store.handleLogin}
            disabled={isInitializing}
          >
            {isInitializing ? (
              <Spinner size="xs" />
            ) : (
              <>
                {isUserLoggedIn ? (
                  <LogOutIcon data-icon="inline-start" />
                ) : undefined}
                {loginBtnTitle}
              </>
            )}
          </Button>
        </div>
      </div>
    )
  }

  private renderCompanySelector = (): JSX.Element => {
    const { isCompanyDropdownShown: showCompanyDropdown } = this.store
    return (
      <div className="row py15 bb-neutral-96">
        <label className="row text large bold brand-dark">
          {Localization.translator.company}
        </label>
        <MenuCloser
          isOpen={showCompanyDropdown}
          closeMenu={this.store.closeCompanyDropdown}
          className="row relative"
        >
          <div
            className="row x-between y-start py8 px12 ba-palette-brand-lighter brada4 pointer active-selector-container"
            onClick={this.store.toggleCompanyDropdown}
          >
            <span className="text extra-large no-bold brand-dark">
              {this.procoreStore.selectedCompany?.name ||
                Localization.translator.select}
            </span>
            <ChevronDownIcon
              size={12}
              className={classList({
                'tw:mt-1': true,
                rotate180: showCompanyDropdown,
              })}
            />
          </div>
          {showCompanyDropdown && this.renderCompanyDropdown()}
        </MenuCloser>
      </div>
    )
  }

  private renderProjectSelector = (): JSX.Element => {
    const { isProjectDropdownShown: showProjectDropdown } = this.store
    const { selectedProjects } = this.procoreStore
    return (
      <div className="row x-between py15 bb-neutral-96">
        <label className="row text large bold brand-dark">
          {Localization.translator.projectSingularPlural}
        </label>
        <MenuCloser
          isOpen={showProjectDropdown}
          closeMenu={this.store.closeProjectDropdown}
          className="row relative"
        >
          <div
            className="row y-start gap-10 py8 px12 ba-palette-brand-lighter brada4 pointer active-selector-container"
            onClick={this.store.toggleProjectDropdown}
          >
            {selectedProjects.length > 0 ? (
              <div className="row wrap gap-5">
                {selectedProjects.map(project =>
                  this.renderSelectedProject(project),
                )}
              </div>
            ) : (
              <span className="text large medium-bold grey">
                {Localization.translator.select}
              </span>
            )}
            <ChevronDownIcon
              size={12}
              className={classList({
                'tw:mt-1': true,
                rotate180: showProjectDropdown,
              })}
            />
          </div>
          {showProjectDropdown && this.renderProjectDropdown()}
        </MenuCloser>
      </div>
    )
  }

  private renderSelectedPill = (
    id: string,
    caption: string,
    onRemove: (id: string) => void,
  ): JSX.Element => (
    <div
      key={id}
      className="flex-none max-width-100 px8 py4 bg-palette-brand-lighter brada4"
    >
      <span className="text large medium-bold brand-dark">{caption}</span>
      <button
        className="ba-none no-outline bg-transparent pointer no-grow"
        onClick={e => {
          e.stopPropagation()
          onRemove(id)
        }}
      >
        <XIcon size={14} className="tw:text-muted-foreground" />
      </button>
    </div>
  )

  private renderSelectedProject = (project: IProcoreProject): JSX.Element =>
    this.renderSelectedPill(project.id, project.name, id =>
      this.store.handleRemoveProject(id),
    )

  private renderSingleSelectDropdown = (
    items: Array<{ id: string; caption: string }>,
    selectedId: string | null | undefined,
    onSelect: (id: string) => void,
    searchQuery?: string,
    onSearchChange?: (query: string) => void,
  ): JSX.Element => {
    const filteredItems = searchQuery
      ? items.filter(item =>
          item.caption.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : items

    return (
      <div className="absolute-top-100 left-0 right-0 bg-white ba-palette-brand-lighter brada4 z-index-1000 beautiful-shadow">
        {onSearchChange && (
          <div className="row h40 px5 py10 bg-neutral-96 ba-palette-brand-lighter brada4 x-end bb-palette-brand-lighter">
            <SearchIcon size={12} className="tw:text-muted-foreground" />
            <input
              type="text"
              placeholder={Localization.translator.search}
              value={searchQuery || ''}
              onChange={e => {
                const value = e.target.value
                onSearchChange(value)
              }}
              className="text large medium-bold brand-dark no-outline bg-transparent ba-none"
              onClick={e => e.stopPropagation()}
            />
          </div>
        )}
        <div className="max-height360 overflow-y-auto">
          {filteredItems.length > 0 ? (
            filteredItems.map(item => {
              const isSelected = selectedId === item.id
              return (
                <div
                  key={item.id}
                  className={classList({
                    'row x-between py12 px12 pointer dropdown-item': true,
                    selected: isSelected,
                  })}
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    onSelect(item.id)
                  }}
                >
                  <span className="text large medium-bold brand-dark">
                    {item.caption}
                  </span>
                  {isSelected && (
                    <CheckIcon size={10} className="tw:text-primary" />
                  )}
                </div>
              )
            })
          ) : (
            <div className="row py12 px12">
              <span className="text large medium-bold grey">
                {Localization.translator.noResults || 'No results found'}
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }

  private renderCompanyDropdown = (): JSX.Element => {
    const { availableCompanies, selectedCompany } = this.procoreStore
    return this.renderSingleSelectDropdown(
      availableCompanies.map(company => ({
        id: company.id,
        caption: company.name,
      })),
      selectedCompany?.id,
      id => this.store.handleCompanySelect(id),
      this.store.companySearchQuery,
      query => this.store.setCompanySearchQuery(query),
    )
  }

  private renderProjectDropdown = (): JSX.Element => {
    const { availableProjectsForSelectedCompany } = this.store
    const selectedProjectIds = this.procoreStore.selectedProjects.map(p => p.id)

    return (
      <MultiSelectDropdown
        items={availableProjectsForSelectedCompany.map(project => ({
          id: project.id,
          caption: project.name,
        }))}
        selectedIds={selectedProjectIds}
        onItemToggle={item => {
          this.store.toggleProjectSelection(item.id)
        }}
        onSelectAll={filteredItems => {
          const filteredIds = filteredItems.map(item => item.id)
          const currentIds = this.procoreStore.selectedProjects.map(p => p.id)
          const newIds = [
            ...currentIds,
            ...filteredIds.filter(id => !currentIds.includes(id)),
          ]
          this.procoreStore.setSelectedProjects(
            newIds,
            this.procoreStore.selectedCompanyId,
          )
        }}
        onDeselectAll={filteredItems => {
          const filteredIds = filteredItems.map(item => item.id)
          const currentIds = this.procoreStore.selectedProjects.map(p => p.id)
          const newIds = currentIds.filter(id => !filteredIds.includes(id))
          this.procoreStore.setSelectedProjects(
            newIds,
            this.procoreStore.selectedCompanyId,
          )
        }}
        title={Localization.translator.selectProjectToSync}
        itemCountLabel={count => Localization.translator.xProjects(count)}
        searchQuery={this.store.searchQuery}
        onSearchChange={query => this.store.setSearchQuery(query)}
      />
    )
  }

  private renderWorkflowsSyncSection = (): JSX.Element => {
    return (
      <div className="bb-neutral-96 py15">
        <div className="row x-between y-center mb15">
          <label className="row text large bold brand-dark">
            {Localization.translator.workflowsFieldMapping}
          </label>
          {this.procoreStore.isUserLoggedIn && (
            <div className="tw:flex tw:items-center relative no-grow">
              <Button
                variant="outline"
                className="add-workflow-button"
                onClick={e => {
                  e.stopPropagation()
                  this.store.addWorkflowMappingTable()
                }}
                disabled={!this.procoreStore.isUserLoggedIn}
              >
                <PlusIcon data-icon="inline-start" />
                {Localization.translator.add}
              </Button>
            </div>
          )}
        </div>
        {this.store.workflowMappingTables.map((workflowTypeId, index) =>
          this.renderWorkflowMappingTable(index, workflowTypeId),
        )}
      </div>
    )
  }

  private renderWorkflowMappingTable = (
    tableIndex: number,
    workflowTypeId: string,
  ): JSX.Element => {
    const procoreFields = this.store.getProcoreDailyLogFields()
    const permitType = workflowTypeId
      ? this.store.resolveLatestPermitType(workflowTypeId)
      : null
    const workflowFields = permitType
      ? this.store.getWorkflowTypeFields(permitType)
      : []
    return (
      <FieldMappingTable
        key={tableIndex}
        leftHeaderRow1={
          <div className="row px10 gap-10">
            <Icons.LogoDark className="row icon-24 no-grow bg-white" />
            <span className="text small medium-bold grey">StruxHub</span>
          </div>
        }
        rightHeaderRow1={
          <div className="row x-between">
            <div className="row px10 gap-10">
              <Icons.ProcoreLogo className="row icon-24 no-grow bg-white" />
              <span className="text small medium-bold grey">
                Procore Daily Log
              </span>
            </div>
            <MenuCloser
              isOpen={this.store.isWorkflowMenuOpen(workflowTypeId)}
              closeMenu={this.store.closeWorkflowMenu}
              className="row relative no-grow"
            >
              <div className="row relative">
                <button
                  className={classList({
                    'row x-center y-center mw32 h20 brada4 pointer ba-none bg-transparent': true,
                    'bg-neutral-92':
                      this.store.isWorkflowMenuOpen(workflowTypeId),
                  })}
                  onClick={e => {
                    e.stopPropagation()
                    this.store.toggleWorkflowMenu(workflowTypeId)
                  }}
                  disabled={!this.procoreStore.isUserLoggedIn}
                >
                  <EllipsisIcon
                    size={16}
                    className="tw:text-muted-foreground"
                  />
                </button>
                {this.store.isWorkflowMenuOpen(workflowTypeId) &&
                  this.renderWorkflowMenu(workflowTypeId, tableIndex)}
              </div>
            </MenuCloser>
          </div>
        }
        leftHeaderRow2={
          <div className="row px10 gap-10">
            {permitType?.iconName ? (
              <div className="row icon-24 no-grow bg-white">
                <WorkflowTypeIcon iconName={permitType.iconName} className="" />
              </div>
            ) : (
              <GenericWorkflowIcon
                size={24}
                className="tw:flex-none! tw:bg-background"
              />
            )}
            <div className="relative">
              {this.renderWorkflowTypeSelectionPanel(tableIndex)}
            </div>
          </div>
        }
        rightHeaderRow2={
          <div className="row x-between">
            <div className="row px10 gap-10">
              <WorkflowTypeIcon
                iconName="delivery"
                className="row icon-24 no-grow bg-white"
              />
              <span className="text small medium-bold grey">
                Delivery Section
              </span>
            </div>
          </div>
        }
        rows={procoreFields.map(procoreField => ({
          key: `workflow-${tableIndex}-${procoreField.name}`,
          left: this.renderWorkflowFieldSelector(
            workflowTypeId,
            procoreField.name,
            workflowFields,
            this.store.isMultipleSelectionField(procoreField.name),
            permitType,
            !workflowTypeId || !this.procoreStore.isUserLoggedIn,
          ),
          right: (
            <span className="text small medium-bold brand-dark">
              {procoreField.displayName}
              {(procoreField.name === 'time' ||
                procoreField.name === 'tracking_number') && (
                <span
                  className="text small medium-bold"
                  style={{ color: '#E53E3E' }}
                >
                  {' *'}
                </span>
              )}
            </span>
          ),
        }))}
      />
    )
  }

  private renderWorkflowTypeForm = (
    onSelect: (workflowTypeId: string) => Promise<void>,
    onClose: () => void,
  ): JSX.Element => {
    return (
      <FormTypeSelectionForm
        onSelect={onSelect}
        onClose={onClose}
        locationAttributesStore={this.props.locationAttributesStore}
        state={this.props.state}
      />
    )
  }

  private renderWorkflowTypeSelectionPanel = (
    tableIndex: number,
  ): JSX.Element => {
    const isOpen = this.store.openWorkflowTypeDropdownIndex === tableIndex
    const workflowTypeId = this.store.workflowMappingTables[tableIndex]
    const permitType = workflowTypeId
      ? this.store.resolveLatestPermitType(workflowTypeId)
      : null
    const workflowTypeName = permitType?.name || ''

    return (
      <Popover
        open={isOpen}
        onOpenChange={open => {
          if (open) {
            this.store.openWorkflowTypeDropdown(tableIndex)
          } else if (this.store.openWorkflowTypeDropdownIndex === tableIndex) {
            // Opening another row's dropdown already moved the index there.
            this.store.closeWorkflowTypeDropdown()
          }
        }}
      >
        <PopoverTrigger
          nativeButton={false}
          disabled={!this.procoreStore.isUserLoggedIn}
          render={<div className="row y-center gap-5 pointer" />}
        >
          <span className="text small medium-bold grey">
            {workflowTypeId
              ? workflowTypeName
              : Localization.translator.selectWorkflowType}
          </span>
          <ChevronDownIcon
            size={12}
            className={classList({ rotate180: isOpen })}
          />
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          className="tw:h-[400px] tw:w-[400px] tw:p-0"
        >
          {this.renderWorkflowTypeForm(async (workflowTypeId: string) => {
            await this.store.setWorkflowTypeForTable(tableIndex, workflowTypeId)
            this.store.closeWorkflowTypeDropdown()
          }, this.store.closeWorkflowTypeDropdown)}
        </PopoverContent>
      </Popover>
    )
  }

  private renderWorkflowMenu = (
    workflowTypeId: string,
    tableIndex: number,
  ): JSX.Element => {
    return (
      <div className="absolute-top-100 mt4 bg-white ba-palette-brand-lighter brada9 z-index-1000 beautiful-shadow">
        <div
          className="row y-center gap-5 py10 px10 pointer no-white-space-wrap"
          onClick={async e => {
            e.stopPropagation()
            await this.store.removeWorkflowMappingTable(tableIndex)
          }}
        >
          <Trash2Icon size={16} className="tw:text-destructive" />
          <span
            className="text large medium-bold no-white-space-wrap"
            style={{ color: '#E53E3E' }}
          >
            {Localization.translator.deleteWorkflowMapping}
          </span>
        </div>
      </div>
    )
  }

  private renderWorkflowFieldSelector = (
    workflowTypeId: string | null,
    dailyLogFieldName: string,
    workflowFields: IWorkflowField[],
    isMultiple: boolean,
    permitType: IPermitType | null,
    isDisabled = false,
  ): JSX.Element => {
    if (!permitType && workflowTypeId) {
      return (
        <div style={{ width: '100%' }}>
          <div className="tw:flex tw:items-center tw:justify-between">
            <span className="tw:flex-1 text small medium-bold grey">
              {Localization.translator.loading || 'Loading...'}
            </span>
            <SettingsIcon className="text grey-30 tw:size-4" />
          </div>
        </div>
      )
    }

    const hasWorkflowType = !!workflowTypeId && !isDisabled && !!permitType
    const isOpen = hasWorkflowType
      ? this.store.isFieldSelectorOpen(workflowTypeId, dailyLogFieldName)
      : false

    const selectedMapping = hasWorkflowType
      ? this.store.getWorkflowTypeFieldMapping(
          workflowTypeId,
          dailyLogFieldName,
        )
      : null

    let selectedFieldIds: string[] = []
    if (hasWorkflowType && workflowFields && Array.isArray(workflowFields)) {
      try {
        selectedFieldIds = this.store.getSelectedFieldIdsFromMapping(
          selectedMapping,
          workflowFields,
        )
      } catch (error) {
        console.error('Error getting selected field IDs from mapping:', error, {
          workflowTypeId,
          dailyLogFieldName,
          selectedMapping,
          workflowFieldsLength: workflowFields?.length,
          permitType: permitType?.id,
        })
        selectedFieldIds = []
      }
    }

    const allowedFieldTypes = null
    const allowedSpecialFieldIds =
      this.store.getAllowedSpecialFieldIds(dailyLogFieldName)

    const fieldFilter = (field: IWorkflowField) =>
      this.store.isFieldAllowedForProcoreField(field, dailyLogFieldName)

    if (isMultiple) {
      return (
        <div style={{ width: '100%' }}>
          <StruxHubFieldSelectorModal
            fields={hasWorkflowType ? workflowFields : []}
            selectedFieldIds={selectedFieldIds}
            isMultiple={true}
            isOpen={isOpen}
            onClose={this.store.closeFieldSelector}
            procoreFieldName={dailyLogFieldName}
            allowedFieldTypes={allowedFieldTypes}
            allowedSpecialFieldIds={allowedSpecialFieldIds}
            fieldFilter={fieldFilter}
            permitType={permitType}
            onFieldToggle={fieldId => {
              if (!workflowFields || !Array.isArray(workflowFields)) return
              const workflowField = workflowFields.find(
                f => f && f.id === fieldId,
              )
              if (!workflowField) return

              const fieldSource = this.store.createFieldSource(
                workflowTypeId,
                workflowField,
              )
              if (!fieldSource) return

              const isSelected =
                Array.isArray(selectedFieldIds) &&
                selectedFieldIds.includes(fieldId)

              if (isSelected) {
                this.store
                  .removeWorkflowTypeFieldMapping(
                    workflowTypeId,
                    dailyLogFieldName,
                    fieldSource,
                  )
                  .catch(error => {
                    console.error(
                      'Failed to remove workflow field mapping:',
                      error,
                    )
                  })
              } else {
                this.store
                  .addWorkflowTypeFieldMapping(
                    workflowTypeId,
                    dailyLogFieldName,
                    fieldSource,
                  )
                  .catch(error => {
                    console.error(
                      'Failed to save workflow field mapping:',
                      error,
                    )
                  })
              }
            }}
            onSelectAll={() => {
              if (!workflowTypeId) return
              const selectableFields = this.store.getFilteredSelectableFields(
                workflowFields,
                dailyLogFieldName,
              )
              this.store
                .handleSelectAllFields(
                  workflowTypeId,
                  dailyLogFieldName,
                  selectableFields,
                  selectedFieldIds,
                )
                .catch(error => {
                  console.error('Failed to select all fields:', error)
                })
            }}
            onDeselectAll={() => {
              if (!workflowTypeId) return
              this.store
                .handleDeselectAllFields(workflowTypeId, dailyLogFieldName)
                .catch(error => {
                  console.error('Failed to deselect all fields:', error)
                })
            }}
            onSelectStepFields={(stepPosition: number) => {
              if (!workflowTypeId) return
              const selectableFields = this.store.getFilteredSelectableFields(
                workflowFields,
                dailyLogFieldName,
              )
              const stepFields = selectableFields.filter(
                f => f.stepPosition === stepPosition,
              )
              this.store
                .handleSelectStepFields(
                  workflowTypeId,
                  dailyLogFieldName,
                  stepPosition,
                  stepFields,
                  selectedFieldIds,
                )
                .catch(error => {
                  console.error('Failed to select step fields:', error)
                })
            }}
            onDeselectStepFields={(stepPosition: number) => {
              if (!workflowTypeId) return
              const selectableFields = this.store.getFilteredSelectableFields(
                workflowFields,
                dailyLogFieldName,
              )
              const stepFields = selectableFields.filter(
                f => f.stepPosition === stepPosition,
              )
              this.store
                .handleDeselectStepFields(
                  workflowTypeId,
                  dailyLogFieldName,
                  stepFields,
                  selectedFieldIds,
                )
                .catch(error => {
                  console.error('Failed to deselect step fields:', error)
                })
            }}
            targetElement={
              <div
                className={classList({
                  'tw:flex tw:items-center tw:justify-between': true,
                  pointer: hasWorkflowType,
                })}
                style={{ width: '100%', height: '100%' }}
                onClick={e => {
                  if (!hasWorkflowType) {
                    e.stopPropagation()
                    return
                  }
                  e.stopPropagation()
                  this.store.toggleFieldSelector(
                    workflowTypeId,
                    dailyLogFieldName,
                  )
                }}
                title={
                  !hasWorkflowType
                    ? Localization.translator.selectWorkflowTypeFirst
                    : undefined
                }
              >
                {selectedFieldIds.length > 0 &&
                workflowFields &&
                Array.isArray(workflowFields) ? (
                  <span className="tw:flex-1 text small medium-bold brand-dark">
                    {selectedFieldIds
                      .map(fieldId => {
                        const field = workflowFields.find(
                          f => f && f.id === fieldId,
                        )
                        return field?.caption
                      })
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                ) : (
                  <span className="tw:flex-1 text small medium-bold grey">
                    {Localization.translator.none}
                  </span>
                )}
                <SettingsIcon className="text grey-30 tw:size-4" />
              </div>
            }
            disabled={!hasWorkflowType}
          />
        </div>
      )
    } else {
      const selectedField =
        selectedFieldIds.length > 0 &&
        workflowFields &&
        Array.isArray(workflowFields)
          ? workflowFields.find(f => f && f.id === selectedFieldIds[0])
          : null

      return (
        <div style={{ width: '100%' }}>
          <StruxHubFieldSelectorModal
            fields={hasWorkflowType ? workflowFields : []}
            selectedFieldIds={selectedFieldIds}
            isMultiple={false}
            isOpen={isOpen}
            onClose={this.store.closeFieldSelector}
            procoreFieldName={dailyLogFieldName}
            allowedFieldTypes={allowedFieldTypes}
            allowedSpecialFieldIds={allowedSpecialFieldIds}
            fieldFilter={fieldFilter}
            onFieldSelect={fieldId => {
              if (!workflowFields || !Array.isArray(workflowFields)) return
              const field = workflowFields.find(f => f && f.id === fieldId)
              if (field) {
                this.handleWorkflowFieldSelect(
                  workflowTypeId,
                  dailyLogFieldName,
                  field,
                )
              }
            }}
            targetElement={
              <div
                className={classList({
                  'tw:flex tw:items-center tw:justify-between': true,
                  pointer: hasWorkflowType,
                })}
                style={{ width: '100%', height: '100%' }}
                onClick={e => {
                  if (!hasWorkflowType) {
                    e.stopPropagation()
                    return
                  }
                  e.stopPropagation()
                  this.store.toggleFieldSelector(
                    workflowTypeId,
                    dailyLogFieldName,
                  )
                }}
                title={
                  !hasWorkflowType
                    ? Localization.translator.selectWorkflowTypeFirst
                    : undefined
                }
              >
                <span
                  className={classList({
                    'tw:flex-1 text small medium-bold': true,
                    'brand-dark': !!selectedField,
                    grey: !selectedField,
                  })}
                >
                  {selectedField?.caption || Localization.translator.none}
                </span>
                <SettingsIcon className="text grey-30 tw:size-4" />
              </div>
            }
            disabled={!hasWorkflowType}
          />
        </div>
      )
    }
  }

  private handleWorkflowFieldSelect = async (
    workflowTypeId: string,
    dailyLogFieldName: string,
    field: IWorkflowField,
  ) => {
    const fieldSource = this.store.createFieldSource(workflowTypeId, field)
    if (!fieldSource) return

    await this.store.updateWorkflowTypeFieldMapping(
      workflowTypeId,
      dailyLogFieldName,
      [fieldSource],
    )
    this.store.closeFieldSelector()
  }
}
